import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { PopupDialog } from '@app/Common/popup/popup.dialog';
import { ToastMessageType, ToastService } from '@app/Common/toast/';
import { TenantService } from '@app/Services/tenant.service';
import { LocationAttributeModalComponent } from '../Modals/location-attribute-modal.component';
import { LocationAttributeOrder } from '../Models';
import { LocationAttribute } from '../Models/location-attribute.model';
import { LocationAttributesService } from '../Services/location-attributes.service';
import { ModuleService } from '../Services/module.service';
import { LocationAttributesConstants as Constants } from './location-attributes.constants';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatSidenavContainer, MatSidenav } from '@angular/material/sidenav';
import { LocationAttributeEditorComponent } from './Editor/location-attribute-editor.component';
import { RouteFilterComponent } from '../../../../Common/route-filter/route-filter.component';
import { MatExpansionPanelTitle } from '@angular/material/expansion';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CallbackPipe } from '../../../../Common/callback.pipe';
import { TranslationPipe } from '../../../../Services/translation.pipe';

@Component({
    selector: 'aw-location-attributes',
    templateUrl: './location-attributes.component.html',
    styleUrls: ['./location-attributes.component.less'],
    imports: [MatSidenavContainer, MatSidenav, LocationAttributeEditorComponent, RouteFilterComponent, MatExpansionPanelTitle, NgClass, ExtendedModule, MatSlideToggle, MatButton, MatIcon, MatIconButton, AsyncPipe, CallbackPipe, TranslationPipe]
})
export class LocationAttributesComponent implements OnInit {
    blockUi = false;
    headerText: string;
    toggleColor = 'accent';
    filterPlaceHolderText: string;
    attributeFilter: string;
    attributes: LocationAttribute[];
    editingAttribute: LocationAttribute[];
    @ViewChild(Constants.rightsidePanelId, { static: true }) rightside: any;
    listAliases: any[];
    drivingAttributes: LocationAttribute[] = [];

    private cannotDeleteField$: Observable<{
        attribute: LocationAttribute;
        reason: string;
    }>;
    private deleteField$: Observable<{ attribute: LocationAttribute }>;
    private editField$: Observable<{ attribute: LocationAttribute }>;
    private editList$: Observable<{ listAlias: string; fieldName: string }>;
    private addList$: Observable<any>;

    private subscriptions: Subscription[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private tenantService: TenantService,
        private moduleService: ModuleService,
        public locationAttributesService: LocationAttributesService,
        public dialog: MatDialog,
        private toast: ToastService
    ) { }

    ngOnInit() {
        this.blockUi = true;
        this.headerText = Constants.headerText;
        this.filterPlaceHolderText = Constants.filterPlaceHolderText;

        this.subscriptions.push(this.locationAttributesService
            .getHierarchyAttributes(this.tenantService.selectedTenant)
            .subscribe((attributes) => {
                this.attributes = attributes;
                this.blockUi = false;
            })
        );

        combineLatest([this.activatedRoute.queryParams]).subscribe(
            ([queryParams]) => {
                this.attributeFilter = queryParams.attributeFilter;
            }
        );

        this.tenantService.getLists().subscribe((r) => {
            this.listAliases = r;
        });

        this.editField$ = this.locationAttributesService.getEditAttribute();
        this.subscriptions.push(
            this.editField$.subscribe((fieldGroup) => {
                if (fieldGroup === null) {
                    return;
                }
                this.rightside.toggle();
            })
        );

        this.deleteField$ = this.locationAttributesService.getDeleteAttribute();
        this.subscriptions.push(
            this.deleteField$.subscribe((field) => {
                if (field !== null) {
                    this.deleteAttribute(field.attribute);
                }
            })
        );

        this.cannotDeleteField$ =
            this.locationAttributesService.getCannotDeleteAttribute$();
        this.subscriptions.push(
            this.cannotDeleteField$.subscribe((field) => {
                if (field !== null) {
                    if (field.reason == Constants.cannotDeleteReasonPromptDeactivate) {
                        this.toggleActive(
                            field.attribute,
                            null,
                            Constants.cannotDeleteDialogText,
                            Constants.cannotDeletePromptDeactivateDialogQuestionText
                        );
                    }
                    else {
                        this.confirm(
                            Constants.cannotDeleteDialogText,
                            Constants.cannotDeleteDialogQuestionText,
                            Constants.ok,
                            Constants.cancel
                        );
                    }
                }
            })
        );

        this.editList$ = this.moduleService.getEditList();
        this.subscriptions.push(
            this.editList$.subscribe(({ listAlias, fieldName }) => {
                this.editList(listAlias, fieldName);
            })
        );

        this.addList$ = this.moduleService.getAddList();
        this.subscriptions.push(
            this.addList$.subscribe(() => {
                this.addList();
            })
        );
    }

    public openFieldModalComponent(attribute: LocationAttribute): void {
        const dialogRef = this.dialog.open(LocationAttributeModalComponent);
        dialogRef.componentInstance.setAttribute(attribute);
        dialogRef.componentInstance.DialogTitle = this.prettifyLabel(
            attribute.AttributeName
        );
    }

    public closeRightSidePanel() {
        this.rightside.close();
    }

    public filterAttributes(
        hierarchyAttribute: LocationAttribute,
        args: string[]
    ) {
        const regex = new RegExp(`(${args[0]})`, 'i');

        return !args[0] || regex.test(hierarchyAttribute.AttributeName);
    }

    public prettifyLabel(lbl: string): string {
        let op: string;
        if (!lbl) {
            return '';
        }
        if (lbl.indexOf(':') > -1) {
            op = lbl.split(':')[1];
        } else {
            op = lbl;
        }

        return op.replace(/_/g, ' ');
    }

    editList(listAlias: string, fieldName: string) {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle =
            Constants.manageListItemsDialogText;
        dialogRef.componentInstance.DialogDescription = `${Constants.editListItemsForDialogText} ${fieldName}`;
        dialogRef.componentInstance.DialogListAlias = listAlias;
        dialogRef.componentInstance.DisplayListEditor = true;

        return dialogRef.afterClosed().subscribe((res) => {
            this.locationAttributesService.notifyListsUpdate();
            return res;
        });
    }

    addList() {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = Constants.addListDialogText;
        dialogRef.componentInstance.DialogDescription = Constants.addListDialogText;
        dialogRef.componentInstance.DialogConfirmButtonText =
            Constants.addListDialogText;
        dialogRef.componentInstance.DialogCancelButtonText = Constants.cancel;
        dialogRef.componentInstance.DisplayListAdd = true;

        return dialogRef.afterClosed().subscribe((res) => {
            if (res?.Result) {
                this.tenantService.addList(res.Text).subscribe({
                    next: (): void => {
                        this.tenantService.getLists().subscribe((r) => {
                            this.listAliases = r;
                            this.locationAttributesService.notifyListsUpdate();
                        });
                    },
                    error: (): void => {
                        this.toast.sendMessage('Unable to Add List',
                            `Could not create list "${res.Text}". It may already exist.`,
                            ToastMessageType.error);
                    }
                });
            }
            return res;
        });
    }

    deleteAttribute(attribute: LocationAttribute): void {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = Constants.deleteDialogText;
        dialogRef.componentInstance.DialogDescription =
            Constants.actionConfirmDialogQuestionText;
        dialogRef.componentInstance.DialogConfirmButtonText = Constants.yes;
        dialogRef.componentInstance.DialogCancelButtonText = Constants.no;

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.Result) {
                this.locationAttributesService
                    .deleteLocationAttribute(attribute, this.tenantService.selectedTenant)
                    .subscribe(() => { });
            }
        });
    }

    setAttributeModified(attribute: LocationAttribute) {
        if (!attribute.IsCustomField) { attribute.Modified = true; }
        this.locationAttributesService
            .updateAttribute(attribute, this.tenantService.selectedTenant)
            .subscribe(() => { });
    }

    toggleActive(
        attribute: LocationAttribute,
        $event: any,
        title: string,
        description: string
    ) {
        const isActive = !attribute.IsDeactivated;
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle =
            title ||
            (isActive
                ? Constants.deactivateDialogText
                : Constants.activateDialogText);
        dialogRef.componentInstance.DialogDescription =
            description || Constants.actionConfirmDialogQuestionText;
        dialogRef.componentInstance.DialogConfirmButtonText = Constants.yes;
        dialogRef.componentInstance.DialogCancelButtonText = Constants.no;

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.Result) {
                this.locationAttributesService
                    .toggleActiveLocationAttribute(
                        attribute,
                        this.tenantService.selectedTenant
                    )
                    .subscribe(() => {
                        attribute.IsDeactivated = !attribute.IsDeactivated;
                    });
            } else if ($event) {
                $event.source.checked = !isActive;
            }
        });
    }

    confirm(
        title: string,
        description: string,
        confirmButtonText: string,
        cancelButtonText: string
    ) {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = title;
        dialogRef.componentInstance.DialogDescription = description;
        dialogRef.componentInstance.DialogConfirmButtonText = confirmButtonText;
        dialogRef.componentInstance.DialogCancelButtonText = cancelButtonText;
    }

    reorderField(attribute: LocationAttribute, direction: ReorderingDirection) {
        const orderedAttributes = this.getOrderedAttributes();
        const currentIndex = orderedAttributes.findIndex(
            (a) => a.LocationAttributeId === attribute.LocationAttributeId
        );
        if (direction === ReorderingDirection.Up && currentIndex === 0) { return; } // Already at the top
        if (
            direction === ReorderingDirection.Down &&
            currentIndex === orderedAttributes.length - 1
        ) { return; } // Already at the bottom

        const attributeToSwapWith =
            direction === ReorderingDirection.Up
                ? orderedAttributes[
                (currentIndex + orderedAttributes.length - 1) %
                orderedAttributes.length
                ]
                : orderedAttributes[(currentIndex + 1) % orderedAttributes.length];
        attribute.Modified = true;
        attributeToSwapWith.Modified = true;
        this.switchAttributePosition(attributeToSwapWith, attribute, direction);
        this.updateOrder(attribute, attributeToSwapWith);
    }

    getOrderedAttributes(): LocationAttribute[] {
        return this.locationAttributesService.attributes.sort(
            (a: LocationAttribute, b: LocationAttribute) => {
                if (a.Order < b.Order) { return -1; }
                if (a.Order > b.Order) { return 1; }
                return 0;
            }
        );
    }

    switchAttributePosition(
        source: LocationAttribute,
        destination: LocationAttribute,
        direction: ReorderingDirection
    ): void {
        let srcOrder = source.Order;
        const destOrder = destination.Order;

        if (srcOrder === destOrder) {
            direction === ReorderingDirection.Up ? srcOrder-- : srcOrder++;
        }

        const fieldInObservable = this.locationAttributesService.attributes.find(
            (x) => x.LocationAttributeId === source.LocationAttributeId
        );

        fieldInObservable.Order = destOrder;
        destination.Order = srcOrder;
    }

    updateOrder(source: LocationAttribute, destination: LocationAttribute): void {
        const attributeOrder = new LocationAttributeOrder();
        attributeOrder.SourceAttributeId = source.LocationAttributeId;
        attributeOrder.SourceAttributeOrder = source.Order;
        attributeOrder.DestinationAttributeId = destination.LocationAttributeId;
        attributeOrder.DestinationAttributeOrder = destination.Order;
        this.locationAttributesService
            .updateOrder(attributeOrder, this.tenantService.selectedTenant)
            .subscribe();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => this.unsubscribe(sub));
    }

    private unsubscribe(subscription: Subscription) {
        if (subscription !== null) {
            subscription.unsubscribe();
        }
    }
}

enum ReorderingDirection {
    Up = 0,
    Down = 1,
}
